<template>
  <q-field
    ref="inputRef"
    v-bind="$attrs"
    class="date-input cursor-pointer"
    :model-value="modelValue"
    clear-icon="close"
    :clearable="clearable"
    @clear="emit('update:modelValue', '')"
    @click="onClick"
  >
    <template
      v-if="$slots.append"
      #append
    >
      <slot name="append" />
    </template>
    <template #prepend>
      <q-icon
        name="img:common/icons/calendar.svg"
        size="24px"
      />
    </template>
    <template
      v-if="modelValue"
      #control
    >
      <div :class="['text-no-wrap', { 'bg-grey-4 chip': withTime }]">
        {{ formatDate(modelValue, { withTime: false }) }}
      </div>

      <div
        v-if="withTime"
        :class="['text-no-wrap q-ml-8', { 'bg-grey-4 chip': withTime }]"
        @click.stop="showTime(true)"
      >
        {{ time ? convertTimeFormat(time) : ' -- : -- ' }}
      </div>
    </template>

    <input
      v-if="$q.platform.is.ios"
      ref="pickerRef"
      v-model="localValue"
      class="picker"
      type="date"
      @input="onInput($event.target.value)"
    >
    <q-popup-proxy
      v-else
      ref="popupRef"
      :no-focus="$q.platform.is.desktop"
    >
      <q-date
        :model-value="modelValue"
        :default-year-month="dYearMonth"
        :first-day-of-week="firstWeekday"
        :options="options"
        :mask="mask"
        minimal
        no-unset
        @update:modelValue="onInput($event, true)"
      />
    </q-popup-proxy>

    <TimeInput
      v-if="withTime"
      ref="timeInputRef"
      :model-value="time"
      @update:modelValue="$emit('update:timeValue', $event)"
    />
  </q-field>
</template>

<script setup>
import { date, useQuasar } from 'quasar';
import {
  ref,
  computed,
  inject,
  watch,
} from 'vue';

import TimeInput from './TimeInput';

import useTimeFormat from '~composables/useTimeFormat';
import useFormatDate from '~composables/useFormatDate';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  clearable: {
    type: Boolean,
    default: true,
  },
  maxDate: {
    type: String,
    default: null,
  },
  minDate: {
    type: String,
    default: null,
  },
  modelValue: {
    type: String,
    default: '',
  },
  withTime: {
    type: Boolean,
    default: false,
  },
  time: {
    type: String,
    default: '',
  },
  defaultYearMonth: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'update:timeValue', 'focus-time']);

const { formatDate } = useFormatDate();
const { convertTimeFormat } = useTimeFormat();

const firstWeekday = inject('firstWeekday', undefined);
const moment = inject('moment');

const $q = useQuasar();

const mask = 'YYYY-MM-DD';

const inputRef = ref(null);
const pickerRef = ref(null);
const popupRef = ref(null);
const timeInputRef = ref(null);
const localValue = ref(null);
const isJustFirstOpened = ref(false);

const dYearMonth = computed(() => {
  if (props.defaultYearMonth) {
    return moment(props.defaultYearMonth, mask).format('YYYY/MM');
  }
  return undefined;
});

function onClick() {
  timeInputRef.value?.hide();
  if ($q.platform.is.ios) {
    if (props.withTime && !props.modelValue) {
      isJustFirstOpened.value = true;
      setTimeout(() => {
        isJustFirstOpened.value = false;
      }, 200);
    }
    pickerRef.value.focus();
  }
}

function showTime(byClickOnLabel = false) {
  popupRef.value?.hide();
  timeInputRef.value.show();
  if (byClickOnLabel) {
    emit('focus-time');
  }
}

function onInput(value, popupClose) {
  const d = moment(value, [mask, 'L']);
  emit('update:modelValue', d.isValid() ? d.format(mask) : '');

  if (props.withTime && d.isValid()) {
    if (isJustFirstOpened.value) {
      // ios sets current date after focus on empty input before user's confirm
      return;
    }

    if (!props.time) {
      showTime();
    }
  }

  if (popupClose) {
    popupRef.value?.hide();
    inputRef.value.blur();
  }
}

function getDate(value) {
  if (!value) return '';

  if (value === 'now') {
    value = Date.now();
  } else {
    value = value.replace(/([+-]\d\d)(\d\d)$/, '$1:$2');
  }

  return date.formatDate(value, mask);
}

function options(value) {
  value = getDate(value);

  if (props.maxDate && props.minDate) {
    return value >= getDate(props.minDate) && value <= getDate(props.maxDate);
  }

  if (props.maxDate) {
    return value <= getDate(props.maxDate);
  }

  if (props.minDate) {
    return value >= getDate(props.minDate);
  }

  return true;
}

watch(() => props.modelValue, (value) => {
  localValue.value = value;
});
</script>

<style lang="scss" scoped>
.chip {
  padding: 2px 6px;
  border-radius: 4px;
}
</style>
